import React, { JSX, useEffect, useState } from 'react';
import InlineSpinner from '../../shared/InlineSpinner';
import { PortaledLayer } from '../../shared/PortaledLayer';
import { DIALOG_OVERLAY_RACE_STACKED } from '../../shared/ui/uiTokens';
import type { EventTransitionHeaderAccent, EventTransitionWarningTone } from '../types/EventsOverviewTypes';
import type { EventStatusTransitionDialogFrameProps } from '../types/EventsOverviewProps';

export type { EventTransitionHeaderAccent, EventTransitionWarningTone, EventStatusTransitionDialogFrameProps };

export default function EventStatusTransitionDialogFrame(
  props: EventStatusTransitionDialogFrameProps,
): JSX.Element | null {
  const {
    open,
    busy,
    onCancel,
    onProceed,
    wide = false,
    extraWide = false,
    titleId,
    eyebrow,
    title,
    subtitle,
    headerAccent,
    warningTitle,
    warningBody,
    warningTone = 'danger',
    operationIntro,
    children,
    confirmPhrase,
    confirmInputId,
    proceedButtonText,
    proceedDisabledExtra = false,
    proceedUseSuccessTone = false,
  } = props;

  const [typedConfirm, setTypedConfirm] = useState('');

  useEffect(() => {
    setTypedConfirm('');
  }, [open, confirmPhrase]);

  if (!open) return null;

  const phraseOk = typedConfirm.trim() === confirmPhrase;
  const canProceed = phraseOk && !busy && !proceedDisabledExtra;
  const primarySuccess = canProceed && proceedUseSuccessTone;
  const maxW = extraWide
    ? 'max-w-[min(1200px,calc(100vw-1.5rem))]'
    : wide
      ? 'max-w-3xl'
      : 'max-w-lg';
  const maxH = extraWide
    ? 'max-h-[min(92vh,56rem)]'
    : wide
      ? 'max-h-[min(90vh,52rem)]'
      : 'max-h-[min(90vh,40rem)]';

  const warningPanelClass =
    warningTone === 'info'
      ? 'flex gap-3 rounded-xl border border-amber-200/90 bg-amber-50/80 px-3.5 py-3 shadow-sm dark:border-amber-700/60 dark:bg-amber-950/30'
      : 'flex gap-3 rounded-xl border border-red-200/90 bg-red-50/80 px-3.5 py-3 shadow-sm dark:border-red-900/60 dark:bg-red-950/30';
  const warningBadgeClass =
    warningTone === 'info'
      ? 'flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-amber-100 text-sm font-bold text-amber-800 ring-1 ring-amber-200/80 dark:bg-amber-900/60 dark:text-amber-100 dark:ring-amber-700/70'
      : 'flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-red-100 text-sm font-bold text-red-700 ring-1 ring-red-200/80 dark:bg-red-900/60 dark:text-red-200 dark:ring-red-800/70';
  const warningTitleClass =
    warningTone === 'info'
      ? 'text-sm font-semibold text-amber-950 dark:text-amber-100'
      : 'text-sm font-semibold text-red-950 dark:text-red-100';
  const warningBodyClass =
    warningTone === 'info'
      ? 'text-xs leading-relaxed text-amber-900/85 dark:text-amber-200/90'
      : 'text-xs leading-relaxed text-red-900/85 dark:text-red-200/90';
  const warningBadgeGlyph = warningTone === 'info' ? 'i' : '!';

  return (
    <PortaledLayer>
      <div
        data-race-stacked-overlay=""
        className={`${DIALOG_OVERLAY_RACE_STACKED} backdrop-blur-[2px] bg-slate-900/45`}
      >
        <div
          className={`flex w-full ${maxW} ${maxH} flex-col overflow-hidden rounded-2xl bg-white dark:bg-slate-900 shadow-2xl ring-1 ring-gray-200 dark:ring-slate-700`}
          role="dialog"
          aria-modal="true"
          aria-labelledby={titleId}
        >
          <div className="relative overflow-hidden border-b border-gray-200 dark:border-slate-700 bg-gradient-to-br from-sky-50 via-white to-slate-50/90 dark:from-slate-800 dark:via-slate-900 dark:to-slate-950 px-5 py-4">
            <div
              className={[
                'absolute left-0 top-0 h-full w-1 rounded-l-2xl',
                headerAccent === 'emerald' ? 'bg-emerald-500' : headerAccent === 'indigo' ? 'bg-indigo-500' : 'bg-amber-500',
              ].join(' ')}
              aria-hidden
            />
            <div className="pl-2">
              <p className="text-[10px] font-bold uppercase tracking-[0.12em] text-gray-500 dark:text-slate-400">{eyebrow}</p>
              <h2 id={titleId} className="mt-0.5 text-lg font-semibold tracking-tight text-gray-900 dark:text-slate-100">
                {title}
              </h2>
              <p className="mt-1 text-sm text-gray-600 dark:text-slate-300 line-clamp-2" title={subtitle}>
                {subtitle}
              </p>
            </div>
          </div>

          <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-5 py-4 text-sm text-gray-700 dark:text-slate-300">
            {warningTitle ? (
              <div className={warningPanelClass}>
                <span className={warningBadgeClass} aria-hidden>
                  {warningBadgeGlyph}
                </span>
                <div className="min-w-0 space-y-1">
                  <p className={warningTitleClass}>{warningTitle}</p>
                  <div className={warningBodyClass}>{warningBody}</div>
                </div>
              </div>
            ) : null}
            {operationIntro ? <div className="space-y-2 text-sm leading-relaxed text-gray-600 dark:text-slate-300">{operationIntro}</div> : null}
            {children}
            <div className="rounded-xl border border-gray-200 dark:border-slate-700 bg-gray-50 dark:bg-slate-800 px-4 py-3">
              <label className="block text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:text-slate-400" htmlFor={confirmInputId}>
                Confirmation
              </label>
              <p className="mt-1 text-xs text-gray-500 dark:text-slate-400">
                Type <span className="font-mono font-semibold text-gray-700 dark:text-slate-300">{confirmPhrase}</span> to confirm.
              </p>
              <input
                id={confirmInputId}
                type="text"
                autoComplete="off"
                className="mt-2 w-full max-w-xs rounded-lg border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2.5 font-mono text-sm tracking-wide text-gray-900 dark:text-slate-100 shadow-sm focus:border-blue-500 dark:focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20 dark:focus:ring-blue-500/30"
                placeholder={confirmPhrase}
                value={typedConfirm}
                onChange={(e) => setTypedConfirm(e.target.value)}
                disabled={busy || proceedDisabledExtra}
              />
            </div>
          </div>

          <div className="flex flex-wrap items-center justify-end gap-2 border-t border-gray-200 dark:border-slate-700 bg-gray-50 dark:bg-slate-800 px-5 py-4">
            <button type="button" className="rounded-lg border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-4 py-2.5 text-sm font-medium text-gray-700 dark:text-slate-300 shadow-sm transition-colors hover:bg-gray-50 dark:hover:bg-slate-800 disabled:opacity-50" onClick={onCancel} disabled={busy}>
              Back
            </button>
            <button
              type="button"
              className={[
                'inline-flex min-h-[2.5rem] min-w-[10rem] items-center justify-center rounded-lg px-4 py-2.5 text-sm font-semibold shadow-sm transition-colors disabled:opacity-50',
                primarySuccess ? 'border border-emerald-600 bg-emerald-600 text-white hover:bg-emerald-700' : 'border border-red-600 bg-red-600 text-white hover:bg-red-700',
              ].join(' ')}
              onClick={() => void onProceed()}
              disabled={!canProceed}
              aria-busy={busy}
              title={!phraseOk && !busy && !proceedDisabledExtra ? `Type ${confirmPhrase} above to enable.` : undefined}
            >
              {busy ? <InlineSpinner /> : proceedButtonText}
            </button>
          </div>
        </div>
      </div>
    </PortaledLayer>
  );
}
